/**
 * Returns true while the user is still typing a dollar amount — allows a
 * trailing decimal point and up to 2 decimal digits, but nothing else.
 * Use this in onChange to silently reject invalid characters.
 *
 * Valid mid-entry examples: "", "1", "12.", "12.5", "12.50", ".9"
 */
export function isValidDollarInput(value: string): boolean {
  return value === '' || /^(\d+\.?\d{0,2}|\.\d{0,2})$/.test(value);
}

/**
 * Strips the decoration a dollar amount picks up when it is copied out of a
 * payer portal or spreadsheet — "$1,234.50" → "1234.50" — so a pasted amount
 * can be validated as the number it is. Anything else is left for the
 * validators to reject.
 */
export function stripCurrencyFormatting(value: string): string {
  return value.replace(/[$,\s]/g, '');
}

/**
 * Returns true if the string is a complete, well-formed dollar amount.
 * Use this to determine whether the final value is ready to save.
 *
 * Valid examples: "0", "1", "12.50", ".99"
 * Invalid examples: "12.", ".", ""
 */
export function isValidDollarAmount(value: string): boolean {
  return /^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(value);
}

/** Formats an in-progress dollar input to two decimal places without losing leading zeroes. */
export function formatDollarAmount(value: string): string {
  const raw = value.trim();
  if (!raw || raw === '.' || !isValidDollarInput(raw)) return value;

  const [whole = '', cents = ''] = raw.split('.', 2);
  return `${whole || '0'}.${cents.padEnd(2, '0')}`;
}

/**
 * Parses a validated dollar string to a float. Returns null for empty or
 * invalid input — pair with isValidDollarAmount before calling.
 */
export function parseDollarAmount(value: string): number | null {
  if (!isValidDollarAmount(value)) return null;
  return parseFloat(value);
}

/**
 * Sums dollar strings in integer cents so repeated addition can't drift
 * (0.1 + 0.2 === 0.30000000000000004 in float math). Values that are not
 * valid dollar input — blank, "." , letters — contribute nothing, and a
 * mid-entry "12." counts as 12.00. Always returns a 2-decimal string;
 * an empty list returns "0.00".
 */
export function sumDollarAmounts(
  values: Array<string | null | undefined>
): string {
  let cents = 0;
  for (const value of values) {
    const raw = (value ?? '').trim();
    if (!raw || raw === '.' || !isValidDollarInput(raw)) continue;
    const [whole = '', frac = ''] = raw.split('.', 2);
    cents +=
      Number(whole || '0') * 100 + Number(frac.padEnd(2, '0').slice(0, 2));
  }
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
}

/**
 * Compares two dollar strings by value, so "100" and "100.00" match.
 * Blank/invalid amounts are only equal to each other.
 */
export function dollarAmountsEqual(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  return (
    parseDollarAmount((a ?? '').trim()) === parseDollarAmount((b ?? '').trim())
  );
}

/**
 * Display form of a money string or number: "2163.56" -> "$2,163.56",
 * "-$1823.26" -> "-$1,823.26". Null, empty and non-numeric values come back
 * unchanged. Display only: sorting still parses the raw string.
 */
export function formatMoneyDisplay<
  T extends string | number | null | undefined,
>(value: T): T | string {
  if (value === null || value === undefined || value === '') return value;
  const cleaned = stripCurrencyFormatting(String(value));
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return value;
  const n = Number(cleaned);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n === 0 ? 0 : n);
}
